import {
  FileMetadata,
  MetadataFieldsJsonb,
} from '../../../../backend/src/integrations/relativity/file-metadata.entity';
import { cn } from '../lib/utils';
import formatDate from '@/helpers/formatDate';
import { Icon } from '@/new/components/icon';
import { Badge } from '@/new/components/shadcn/badge';
import { Label } from '@/new/components/shadcn/label';

interface MetadataDisplayProps {
  metadata: FileMetadata;
  compact?: boolean; // Use single column layout for popovers
  includeWexlerFields?: boolean; // Include Wexler fields in the display
  maxFields?: number; // Limit number of fields shown (for compact preview)
}

// Helper function to convert camelCase to readable labels
export const formatFieldLabel = (fieldName: string): string => {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

// Helper function to format file size
const formatFileSize = (bytes: number | null | undefined): string | null => {
  if (bytes === null || bytes === undefined) return null;
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

// Infer field type from value
const inferFieldType = (value: unknown): 'text' | 'date' | 'array' | 'number' => {
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'number') return 'number';
  // Check if string looks like a date (ISO format)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return 'date';
  return 'text';
};

export type FieldData = { label: string; value: unknown; type: 'text' | 'date' | 'array' | 'number' };

export interface RenderFieldValueOptions {
  badgeClassName?: string;
  textClassName?: string;
  maxTextLength?: number;
  emptyText?: string;
  compact?: boolean;
}

/**
 * Renders a field value based on its type.
 * Exported for reuse in other components that display metadata fields.
 */
export const renderFieldValue = (field: FieldData, options: RenderFieldValueOptions = {}): React.ReactNode => {
  const {
    badgeClassName = 'bg-gray-100 text-gray-700 text-xs',
    textClassName,
    maxTextLength = 100,
    emptyText = 'N/A',
    compact = false,
  } = options;

  const baseTextClass = compact ? 'text-xs text-gray-900' : 'text-sm text-gray-900';
  const textClass = cn(baseTextClass, textClassName);

  switch (field.type) {
    case 'date':
      return <div className={textClass}>{formatDate(field.value as Date | string)}</div>;
    case 'array':
      if (Array.isArray(field.value) && field.value.length > 0) {
        return (
          <div className="flex flex-wrap gap-1">
            {field.value.map((item, index) => (
              <Badge key={index} className={badgeClassName}>
                {String(item)}
              </Badge>
            ))}
          </div>
        );
      }
      // Handle single value that should be displayed as array
      if (field.value !== null && field.value !== undefined && !Array.isArray(field.value)) {
        return (
          <div className="flex flex-wrap gap-1">
            <Badge className={badgeClassName}>{String(field.value)}</Badge>
          </div>
        );
      }
      return <div className={cn(textClass, 'text-gray-500')}>{emptyText}</div>;
    case 'number':
      return (
        <div className={textClass}>
          {typeof field.value === 'number' ? field.value.toLocaleString() : String(field.value ?? '')}
        </div>
      );
    case 'text':
    default:
      const textValue =
        typeof field.value === 'object' && field.value !== null ? JSON.stringify(field.value) : String(field.value);
      if (textValue.length > maxTextLength) {
        return (
          <div className={cn(textClass, 'break-words')} title={textValue}>
            {textValue.substring(0, maxTextLength)}...
          </div>
        );
      }
      return <div className={cn(textClass, 'break-words')}>{textValue}</div>;
  }
};

// Build display fields from JSONB fields
export const buildFieldsArray = (fields: MetadataFieldsJsonb): FieldData[] => {
  const result: FieldData[] = [];

  Object.entries(fields).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    // Special handling for fileSize
    if (key === 'fileSize') {
      result.push({ label: formatFieldLabel(key), value: formatFileSize(value as number), type: 'text' });
      return;
    }

    result.push({ label: formatFieldLabel(key), value, type: inferFieldType(value) });
  });

  return result;
};

// Color schemes for different metadata sources
type ColorScheme = 'purple' | 'gray';

const colorSchemes: Record<ColorScheme, { bg: string; border: string; text: string; badge: string }> = {
  purple: {
    bg: 'bg-purple-50/30',
    border: 'border-purple-200',
    text: 'text-purple-600',
    badge: 'bg-purple-100/60 text-purple-700',
  },
  gray: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-600',
    badge: 'bg-gray-100 text-gray-700',
  },
};

interface MetadataSectionProps {
  label?: string;
  icon?: string;
  fields: FieldData[];
  colorScheme: ColorScheme;
  compact: boolean;
}

const MetadataSection = ({ label, icon, fields, colorScheme, compact }: MetadataSectionProps) => {
  const colors = colorSchemes[colorScheme];
  const fieldOptions: RenderFieldValueOptions = {
    badgeClassName: `text-xs ${colors.badge}`,
    emptyText: 'None',
    // More aggressive truncation in compact mode
    maxTextLength: compact ? 40 : 100,
    compact,
  };

  return (
    <div className={label ? 'space-y-2' : ''}>
      {label && icon && (
        <Label className={`flex items-center gap-2 text-xs uppercase ${colors.text}`}>
          <Icon className={`${icon} h-3 w-3`} />
          {label}
        </Label>
      )}
      <div
        className={`rounded-md border ${colors.border} ${colors.bg} ${
          compact ? 'grid grid-cols-1 gap-y-2 p-3' : 'grid grid-cols-2 gap-x-6 gap-y-4 p-4'
        }`}
      >
        {fields.map((field, index) => (
          <div key={index} className="min-w-0 space-y-0.5">
            <Label
              className={`font-medium tracking-wide text-gray-500 uppercase ${compact ? 'text-[10px]' : 'text-xs'}`}
            >
              {field.label}
            </Label>
            {renderFieldValue(field, fieldOptions)}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Helper function to extract Wexler fields for inline display.
 * Use this to render Wexler metadata inline with other document info.
 */
export const getWexlerFieldsForDisplay = (metadata: FileMetadata): FieldData[] => {
  return buildFieldsArray(metadata.wexlerFields ?? {});
};

export const MetadataDisplay = ({
  metadata,
  compact = false,
  includeWexlerFields = false,
  maxFields,
}: MetadataDisplayProps) => {
  // Build Relativity fields from dedicated columns and JSONB fields
  let relativityFields: FieldData[] = [];

  // Add dedicated date columns first (if they have values)
  if (metadata.emailSentDate) {
    relativityFields.push({ label: 'Email Sent Date', value: metadata.emailSentDate, type: 'date' });
  }
  if (metadata.documentDate) {
    relativityFields.push({ label: 'Document Date', value: metadata.documentDate, type: 'date' });
  }

  // Add all Relativity JSONB fields
  relativityFields.push(...buildFieldsArray(metadata.relativityFields ?? {}));

  // Apply maxFields limit if specified
  const totalFieldCount = relativityFields.length;
  if (maxFields !== undefined && relativityFields.length > maxFields) {
    relativityFields = relativityFields.slice(0, maxFields);
  }
  const hiddenFieldCount = totalFieldCount - relativityFields.length;

  // Build Wexler fields from wexlerFields JSONB (only when includeWexlerFields is true)
  const wexlerFields = includeWexlerFields ? buildFieldsArray(metadata.wexlerFields ?? {}) : [];

  const hasRelativity = relativityFields.length > 0;
  const hasWexler = wexlerFields.length > 0;

  // If no fields to display, don't render anything
  if (!hasRelativity && !hasWexler) {
    return null;
  }

  return (
    <div className={compact ? 'max-h-80 w-fit max-w-xs min-w-48 space-y-3 overflow-y-auto' : 'space-y-6'}>
      {/* Wexler Section - Gray themed, no title (shown first when includeWexlerFields is true) */}
      {hasWexler && <MetadataSection fields={wexlerFields} colorScheme="gray" compact={compact} />}

      {/* Relativity Section - Purple themed */}
      {hasRelativity && (
        <MetadataSection
          label="Relativity Metadata"
          icon="lucide--database"
          fields={relativityFields}
          colorScheme="purple"
          compact={compact}
        />
      )}

      {/* Show hidden field count if truncated */}
      {hiddenFieldCount > 0 && <p className="text-center text-xs text-gray-400">+{hiddenFieldCount} more fields</p>}
    </div>
  );
};
